const SHIPS = 'ships';
const DESTINATIONS = 'destinations';
const PORTS = 'ports';

export default (options, async = 0) => ({
  searching: false,
  master: null,
  localDestinations: [],
  destinations: [],
  ports: [],
  port: null,
  prices: [],
  fares: [],
  ships: [],
  selectedPrice: null,
  selectedDates: [],
  selectedDestinations: [],
  selectedPorts: [],
  selectedPort: [],
  selectedShips: [],
  selectedFares: [],
  disableWatchers: false,

  get filteredDestinations() {
    if (!this.localDestinations.length || this.master === DESTINATIONS) {
      return this.destinations;
    }
    return this.destinations.filter((destination) => {
      const destinationValues = Object.values(destination.destinations);
      return destinationValues.some((value) =>
        this.localDestinations.includes(value)
      );
    });
  },

  get filteredPorts() {
    if (!this.localDestinations.length || this.master === PORTS) {
      return this.ports;
    }
    return this.ports.filter((port) => {
      const destinationValues = Object.values(port.destinations);
      return this.localDestinations.some((value) =>
        destinationValues.includes(value)
      );
    });
  },

  get filteredShips() {
    if (!this.localDestinations.length || this.master === SHIPS) {
      return this.ships;
    }
    return this.ships.filter((ship) => {
      const destinationValues = Object.values(ship.destinations);
      return this.localDestinations.some((value) =>
        destinationValues.includes(value)
      );
    });
  },

  get selectedDestinationsTitles() {
    const parsedSelectedDestinations = this.parseAndFlattenDestinations();

    return this.destinations
      .filter((destination) => {
        const destinationValues = Object.values(destination.destinations);
        return destinationValues.some((value) =>
          parsedSelectedDestinations.includes(value)
        );
      })
      .map((destination) => destination.title);
  },

  get selectedPricesTitle() {
    return this.selectedPrice
      ? this.prices[this.selectedPrice]
      : this.prices['0'];
  },

  get selectedMoreTitles() {
    return [
      ...this.mapSelectedToTitles(
        this.ships,
        this.selectedShips,
        'ship_code',
        'title'
      ),
      ...this.mapSelectedToTitles(
        this.ports,
        this.selectedPorts,
        'port_code',
        'title'
      ),
      ...(this.selectedPrice !== null ? [this.prices[this.selectedPrice]] : []),
    ];
  },

  headerSize(size) {
    this.sizes[size] = this.open ? this.height : 0;
  },

  mapSelectedToTitles(array, selectedArray, filterKey, mapKey) {
    return array
      .filter((item) => selectedArray.includes(item[filterKey]))
      .map((item) => item[mapKey]);
  },

  updateLocalDestinationsBasedOnMaster() {
    switch (this.master) {
      case DESTINATIONS:
      this.localDestinations = this.parseAndFlattenDestinations();
        break;
      case PORTS:
        this.localDestinations = this.ports
          .filter((item) => this.selectedPorts.includes(item['port_code']))
          .map((item) => item['destinations'])
          .flat();
        break;
      case SHIPS:
        this.localDestinations = this.ships
          .filter((item) => this.selectedShips.includes(item['ship_code']))
          .map((item) => item['destinations'])
          .flat();
        break;
      default:
        this.localDestinations = [];
    }
    this.localDestinations = [...new Set(this.localDestinations)];
  },

  updateURLSearchParams(params, paramName, items) {
    if (items.length > 0) {
      // Join the items into a comma-separated string and set the parameter
      params.set(paramName, items.join(','));
    } else {
      // If the items array is empty, remove the parameter
      params.delete(paramName);
    }
  },

  getUrlQuery() {
    let params = new URLSearchParams();

    const urlParams = new URLSearchParams(window.location.search);
    const sort = urlParams.get('sort');
    const sortQuery = sort ? '&sort=' + sort : '';

    //dispatch filter
    this.$dispatch('set-filter', {
      dates: this.selectedDates,
      destinations: this.parseAndFlattenDestinations(),
      ports: this.selectedPorts,
      port: this.selectedPort,
      ships: this.selectedShips,
      fares: this.selectedFares,
      price: this.selectedPrice,
      sort: sort,
    });

    // Reset the 'page' parameter to its initial value (e.g., 1)
    params.set('page', '1');

    this.updateURLSearchParams(params, 'dates', this.selectedDates);
    this.updateURLSearchParams(
      params,
      'destinations',
      this.parseAndFlattenDestinations()
    );
    this.updateURLSearchParams(params, 'ports', this.selectedPorts);
    this.updateURLSearchParams(params, 'port', this.selectedPort);
    this.updateURLSearchParams(params, 'ships', this.selectedShips);
    this.updateURLSearchParams(params, 'fares', this.selectedFares);

    if (this.selectedPrice !== null) {
      params.set('price', this.selectedPrice);
    }

    // Add 'master' to the URL parameters if it's not null
    if (this.master !== null) {
      params.set('master', this.master);
    }

    return params.toString()
      ? '?' + params.toString() + sortQuery
      : '' + sortQuery;
  },

  async createAndRedirect(url) {
    const query = this.getUrlQuery();
    if (async) {
      this.searching = true;
      await this.$store.cruises.fetch(query);
      this.searching = false;
    } else {
      window.location.href = url + query;
    }
    this.$dispatch('filter:apply');
  },

  async resetFilter() {
    await this.withWatchersDisabled(async () => {
      // Reset arrays using a loop
      [
        'selectedDates',
        'selectedDestinations',
        'selectedPorts',
        'selectedShips',
      ].forEach((key) => {
        this[key] = [];
      });

      // Reset other properties
      this.selectedPrice = null;
      this.master = null;
      this.updateLocalDestinationsBasedOnMaster();

      this.searching = true;
      try {
        const query = this.getUrlQuery();
        await this.$store.cruises.fetch(query);
      } catch (error) {
        console.error('Error fetching cruises:', error);
        // Handle error as needed
      } finally {
        this.searching = false;
      }
    });
    this.$store.cruises.resetFilter();
  },

  // Utility function to disable watchers, perform an action, and re-enable watchers
  async withWatchersDisabled(action) {
    this.disableWatchers = true;
    try {
      await action();
    } finally {
      this.disableWatchers = false;
    }
  },

  prefetch() {
    this.$store.cruises.fetch(this.getUrlQuery(), true);
  },

  parseAndFlattenDestinations() {
    //return this.selectedDestinations.flatMap((dest) => JSON.parse(dest));
    return this.selectedDestinations.flatMap((dest) => {
      // Check if 'dest' is a string and try to parse it as JSON
      if (typeof dest === 'string') {
        try {
          const parsed = JSON.parse(dest);
          // If parsed is an array, return it; if it's an object, return its values
          return Array.isArray(parsed) ? parsed : Object.values(parsed);
        } catch (e) {
          // If parsing fails, assume 'dest' is a regular string
          return dest;
        }
      }
      // If 'dest' is not a string, assume it's an object and return its values
      else if (typeof dest === 'object') {
        return Object.values(dest);
      }
      // If 'dest' is neither a string nor an object, return an empty array
      return [];
    });
  },

  init() {
    // Initialize from options
    this.destinations = options.destinations || [];
    this.ports = options.ports || [];
    this.ships = options.ships || [];
    this.prices = options.prices || [];
    this.fares = options.fares || [];

    // Create a URLSearchParams object from the current URL
    const params = new URLSearchParams(window.location.search);

    // Update selectedDates
    const dateString = params.get('dates');
    this.selectedDates = dateString ? dateString.split(',') : [];

    // Update selectedDestinations
    const urlDestinations = params.get('destinations')
      ? params.get('destinations').split(',')
      : [];
    // Iterate through destinations array
    this.destinations.forEach((destination) => {
      // Check if any URL destination is included in the destination object's destinations property
      if (
        urlDestinations.some((urlDest) =>
          destination.destinations.includes(urlDest)
        )
      ) {
        // Push the destinations property to selectedDestinations
        this.selectedDestinations.push(
          JSON.stringify(destination.destinations)
        );
      }
    });

    // Similarly, update selectedPorts and selectedShips
    this.selectedPorts = params.get('ports')
      ? params.get('ports').split(',')
      : [];
    this.selectedShips = params.get('ships')
      ? params.get('ships').split(',')
      : [];

    this.selectedFares = params.get('fares')
      ? params.get('fares').split(',')
      : [];

    // Update selectedPrice
    this.selectedPrice = params.get('price') || null;

    // Set master from URL
    this.master = params.get('master');

    // Call updateLocalDestinationsBasedOnMaster to set localDestinations
    this.updateLocalDestinationsBasedOnMaster();

    const resetMasterIfNoFilters = () => {
      if (
        !this.selectedDestinations.length &&
        !this.selectedPorts.length &&
        !this.selectedShips.length &&
        !this.selectedFares.length
      ) {
        this.master = null;
      }
    };

    this.$watch('selectedDestinations', (value, oldValue) => {
      if (value.length && !oldValue.length && this.master === null) {
        this.master = DESTINATIONS;
      } else {
        resetMasterIfNoFilters();
      }
      this.updateLocalDestinationsBasedOnMaster();
      if (!this.disableWatchers) {
        this.prefetch();
      }
    });

    this.$watch('selectedPorts', (value, oldValue) => {
      if (value.length && !oldValue.length && this.master === null) {
        this.master = PORTS;
      } else {
        resetMasterIfNoFilters();
      }
      this.updateLocalDestinationsBasedOnMaster();
      if (!this.disableWatchers) {
        this.prefetch();
      }
    });

    this.$watch('selectedShips', (value, oldValue) => {
      if (value.length && !oldValue.length && this.master === null) {
        this.master = SHIPS;
      } else {
        resetMasterIfNoFilters();
      }
      this.updateLocalDestinationsBasedOnMaster();
      if (!this.disableWatchers) {
        this.prefetch();
      }
    });

    setTimeout(() => {
      this.$store.cruises.setFilter(window.location.search);
    }, 600);
  },
});
